import type { ApiError, AuthTokens } from '@mcgames/contracts';
import { clearSession, getAccessToken, getRefreshToken, setTokens } from './auth-store.ts';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/** A non-2xx response; `body` is the API's error body (synthesized when the server sent none). */
export class HttpError extends Error {
  constructor(
    readonly status: number,
    readonly body: ApiError,
  ) {
    super(body.message);
    this.name = 'HttpError';
  }

  get code(): ApiError['code'] {
    return this.body.code;
  }
}

/** The request never produced a response. */
export class NetworkError extends Error {
  constructor(options?: ErrorOptions) {
    super('Network request failed', options);
    this.name = 'NetworkError';
  }
}

const AUTH_PREFIX = '/api/auth/';

function isAuthCall(path: string): boolean {
  return path.startsWith(AUTH_PREFIX);
}

async function send(
  method: HttpMethod,
  path: string,
  body: unknown,
  token: string | null,
): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (token !== null) headers['Authorization'] = `Bearer ${token}`;
  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  try {
    return await fetch(path, { method, headers, body: payload });
  } catch (cause) {
    throw new NetworkError({ cause });
  }
}

async function parseError(response: Response): Promise<HttpError> {
  let body: ApiError | undefined;
  try {
    const parsed: unknown = JSON.parse(await response.text());
    if (typeof parsed === 'object' && parsed !== null && 'code' in parsed) {
      body = parsed as ApiError;
    }
  } catch {
    // Not JSON (for example a proxy error page); fall through to the synthesized body.
  }
  return new HttpError(
    response.status,
    body ?? {
      statusCode: response.status,
      code: response.status === 401 ? 'UNAUTHORIZED' : 'UPSTREAM_ERROR',
      message: response.statusText || `HTTP ${response.status}`,
    },
  );
}

async function parseSuccess<T>(response: Response): Promise<T> {
  const text = response.status === 204 ? '' : await response.text();
  return (text === '' ? undefined : JSON.parse(text)) as T;
}

let refreshing: Promise<void> | null = null;

/** A 4xx answer is final: the server understood the request and said no. 5xx and network failures are not. */
export function isDefinitiveRejection(error: unknown): boolean {
  return error instanceof HttpError && error.status >= 400 && error.status < 500;
}

const REFRESH_LOCK = 'mcgames.refresh';

/**
 * Exchanges the stored refresh token for a new pair. Concurrent callers share one request, because
 * a second use of the same refresh token would be treated as token theft. Tabs of the same browser
 * take turns through a Web Lock (when available), so a tab never refreshes with a token another
 * tab has just rotated. A rejected refresh ends the session; a network failure keeps it so a later
 * attempt can still succeed.
 */
export function refreshTokens(): Promise<void> {
  refreshing ??= withRefreshLock(doRefresh).finally(() => {
    refreshing = null;
  });
  return refreshing;
}

/** Resolves once no refresh of this tab is in flight; its outcome does not matter. */
export async function refreshSettled(): Promise<void> {
  while (refreshing !== null) {
    await refreshing.catch(() => undefined);
  }
}

/** Runs `task` while no tab of this browser refreshes (through a Web Lock, when available). */
export async function withRefreshLock<T>(task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  return locks ? await locks.request(REFRESH_LOCK, task) : task();
}

/** Another tab may replace the stored token while a request is in flight: adopt it once. */
const MAX_REFRESH_ATTEMPTS = 2;

async function doRefresh(): Promise<void> {
  for (let attempt = 1; ; attempt++) {
    // Read only now, after the lock was granted: this is the newest token any tab stored.
    const refreshToken = getRefreshToken();
    if (refreshToken === null) {
      clearSession();
      throw new HttpError(401, {
        statusCode: 401,
        code: 'UNAUTHORIZED',
        message: 'No refresh token',
      });
    }
    try {
      setTokens(await http<AuthTokens>('POST', '/api/auth/refresh', { refreshToken }));
      return;
    } catch (error) {
      if (!isDefinitiveRejection(error)) throw error;
      const replacedElsewhere = getRefreshToken() !== refreshToken;
      if (replacedElsewhere && attempt < MAX_REFRESH_ATTEMPTS) continue;
      // Never delete a token this tab did not use: it belongs to a session another tab holds.
      clearSession({ onlyRefreshToken: refreshToken });
      throw error;
    }
  }
}

/**
 * JSON request against the API. Attaches the in-memory access token; on a 401 for such a request it
 * refreshes the session once and retries once. Any 2xx resolves, with `undefined` for an empty body.
 */
export async function http<T>(method: HttpMethod, path: string, body?: unknown): Promise<T> {
  const token = isAuthCall(path) ? null : getAccessToken();
  let response = await send(method, path, body, token);
  if (response.status === 401 && token !== null) {
    // Another request may have refreshed while this one was in flight.
    if (getAccessToken() === token) {
      try {
        await refreshTokens();
      } catch (error) {
        if (!isDefinitiveRejection(error)) throw error;
        // The session is gone; report the original 401.
        throw await parseError(response);
      }
    }
    response = await send(method, path, body, getAccessToken());
  }
  if (!response.ok) throw await parseError(response);
  return parseSuccess<T>(response);
}
